/**
 * One manifest per tool, browser or server (spec 2026-10-18 AI companion growth
 * architecture, sections 4.1 and 4.4). The lists that used to name tools (the
 * effect table, the always-ask set, the trader-data list, the panel gates) are
 * read from these tags instead, so a new tool is described once and every list
 * follows.
 *
 * Types and one pure check, with no imports: companion-api takes this file with
 * `import type` (its image has no copy of this package), so nothing here may be
 * needed at runtime there.
 */

/** What a tool does. */
export const OPERATIONS = [
  'read',
  'list',
  'add',
  'remove',
  'change',
  'navigate',
  'run',
  'explain',
] as const
export type Operation = (typeof OPERATIONS)[number]

/**
 * What a call does to the trader's workspace (2026-10-15, one-assistant
 * Phase 2):
 *  - `read`: changes nothing (lookups, answers, a language preference).
 *  - `view`: moves what the trader sees; nothing is saved (navigation, the
 *    focus date).
 *  - `undoable`: applies a change that a working Undo restores.
 *  - `confirm`: asks before it runs: destructive, replaces something, saves
 *    something with no inverse here, or spends quota.
 */
export const EFFECTS = ['read', 'view', 'undoable', 'confirm'] as const
export type ToolEffect = (typeof EFFECTS)[number]

/** Whose data a tool reads: market data, the trader's own, or the app's. */
export const DATA_OWNERS = ['market', 'trader', 'app'] as const
export type DataOwner = (typeof DATA_OWNERS)[number]

/** Where a tool executes. A property of the tool, not a routing decision. */
export const SURFACES = ['browser', 'server'] as const
export type ToolSurface = (typeof SURFACES)[number]

/** What a tool takes as an argument, or offers the trader as chat choices. */
export const ENTITY_TYPES = [
  'ticker',
  'watchlist',
  'chart',
  'panel',
  'indicator',
  'strategy',
] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

/**
 * Capabilities many panels share (spec 4.4). A panel declares the ones it
 * implements; a tool for one names it instead of listing panel types.
 */
export const SHARED_CAPABILITIES = ['filters', 'columns', 'ticker', 'focus_date', 'link'] as const
export type SharedCapability = (typeof SHARED_CAPABILITIES)[number]

export interface ToolManifest {
  name: string
  /** One or more domain ids from the domain registry (domains.ts); the first is its index group. */
  domains: string[]
  /** Panel types it reads or changes; empty when it is not about one panel type. */
  panels: string[]
  /**
   * The shared capability it acts on, in place of `panels`: it reaches every
   * panel (or page) that declares the capability.
   */
  capability?: SharedCapability
  operations: Operation[]
  effect: ToolEffect
  /** Asks even when the trader turned on "apply without asking". */
  alwaysAsk?: boolean
  dataOwner: DataOwner
  surface: ToolSurface
  takes?: EntityType[]
  /** Entity types it can offer as chat choices. */
  offers?: EntityType[]
  /** Loaded with its full schema on every turn of its surface. */
  core?: boolean
  /** One line for the catalog. The full schema loads on demand. */
  summary: string
}

/**
 * What a panel type (or a page) implements of each shared capability: the
 * operations it supports. History's page-wide filter adds and changes, and has
 * no clear.
 */
export type CapabilityDeclaration = Partial<Record<SharedCapability, readonly Operation[]>>

/**
 * Whether a surface's declaration covers a capability tool: it declares the
 * tool's capability with every operation the tool performs.
 */
export function declarationCovers(
  declared: CapabilityDeclaration | undefined,
  m: Pick<ToolManifest, 'capability' | 'operations'>,
): boolean {
  const ops = m.capability ? declared?.[m.capability] : undefined
  return !!ops && m.operations.every((op) => ops.includes(op))
}

/** A manifest as a tool carries it: its registry supplies the name and the surface. */
export type ToolTags = Omit<ToolManifest, 'name' | 'surface'>

/** One tool on the one tool list (spec 11.1): its schema and its manifest. */
export interface CatalogTool {
  name: string
  description: string
  input_schema: Record<string, unknown>
  manifest: ToolManifest
}

/**
 * The browser half of the tool list: every tool the app registers, panel sets
 * included, and the shared capabilities each panel type and page declares.
 * Generated (browser-tool-catalog.generated.json) because neither companion-api
 * nor the admin portal can load the app's panel tool sets.
 */
export interface BrowserToolCatalog {
  tools: CatalogTool[]
  panel_capabilities: Record<string, CapabilityDeclaration>
  page_capabilities: Record<string, CapabilityDeclaration>
}

const SNAKE = /^[a-z][a-z0-9_]*$/
const KEBAB = /^[a-z][a-z0-9-]*$/
const MAX_SUMMARY = 160
/** Never in our text (house rule); spelled by code point so the source never holds one. */
const EM_DASH = String.fromCharCode(0x2014)

/** Whether a value read off the wire is one of a closed list of names (ENTITY_TYPES, say). */
export function oneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value)
}

/**
 * Every problem with a manifest, each naming the tool. Empty when it is sound.
 * Given the domain registry's ids (DOMAIN_IDS in domains.ts, or companion-api's
 * generated copy), a domain missing from it is a problem (spec 7); without
 * them only each domain's shape is checked.
 */
export function validateManifest(m: ToolManifest, domains?: ReadonlySet<string>): string[] {
  const problems: string[] = []
  const flag = (problem: string) => problems.push(`${m.name}: ${problem}`)

  if (!SNAKE.test(m.name)) flag('the name must be snake case')
  if (m.domains.length === 0) flag('it names no domain')
  for (const d of m.domains) {
    if (!SNAKE.test(d)) flag(`domain "${d}" must be snake case`)
    else if (domains && !domains.has(d)) flag(`unknown domain "${d}"`)
  }
  if (m.operations.length === 0) flag('it names no operation')
  for (const op of m.operations) if (!oneOf(OPERATIONS, op)) flag(`unknown operation "${op}"`)
  if (new Set(m.operations).size !== m.operations.length) flag('an operation is listed twice')
  if (!oneOf(EFFECTS, m.effect)) flag(`unknown effect "${m.effect}"`)
  if (!oneOf(DATA_OWNERS, m.dataOwner)) flag(`unknown data owner "${m.dataOwner}"`)
  if (!oneOf(SURFACES, m.surface)) flag(`unknown surface "${m.surface}"`)

  const summary = m.summary.trim()
  if (!summary) flag('the summary is empty')
  if (summary.includes('\n')) flag('the summary must be one line')
  if (summary.length > MAX_SUMMARY) flag(`the summary is over ${MAX_SUMMARY} characters`)
  if (summary.includes(EM_DASH)) flag('the summary has an em dash')

  for (const t of m.takes ?? []) if (!oneOf(ENTITY_TYPES, t)) flag(`it takes unknown "${t}"`)
  for (const t of m.offers ?? []) if (!oneOf(ENTITY_TYPES, t)) flag(`it offers unknown "${t}"`)

  if (m.capability !== undefined && !oneOf(SHARED_CAPABILITIES, m.capability))
    flag(`unknown capability "${m.capability}"`)
  if (m.capability !== undefined && m.panels.length > 0)
    flag('a capability tool reaches its panels through the declarations; list no panels')
  for (const p of m.panels) if (!KEBAB.test(p)) flag(`panel type "${p}" must be kebab case`)

  if (m.alwaysAsk && (m.effect === 'read' || m.effect === 'view'))
    flag('a tool that changes nothing cannot always ask')
  return problems
}
